import { DropdownOption } from 'components/final-form/dropdownUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { DropdownProps, Grid, Header, Icon, Table } from 'semantic-ui-react';
import styled from 'styled-components';
import { RoleDto, UserRoleDto } from 'ts-types/api.types';
import { PxDropdownSc } from 'components/final-form/Select';

const StyledIcon = styled(Icon)`
  cursor: pointer;
`;

const StyledBtnContainer = styled.div`
  margin-bottom: 5px;
  white-space: nowrap !important;

  & i.icon:last-child {
    margin: 0;
  }
`;

const StyledButtons = styled(Grid.Column)`
  padding: 0 !important;
  flex: 5 5 auto;
`;

const StyledTableRight = styled(Grid.Column)`
    padding-right: 1rem !important;
    padding-left: 0 !important;
    flex: 45 45 auto;
`;

const StyledTableLeft = styled(Grid.Column)`
    padding-left: 1rem !important;
    padding-right: 0 !important;
    flex: 45 45 auto;
`;

const StyledTableRow = styled(Table.Row)`
  cursor: pointer;
`;

const ScrollableTableWrapper = styled.div`
  height: 375px;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  border: 1px solid rgba(34,36,38,.15);
  box-shadow: none;
  border-radius: .28571429rem;
  flex: 50 50 auto;

  & .ui.table {
    border: none;
  }

   & .ui.table thead tr:first-child > th {
      position: sticky !important;
      top: 0;
      z-index: 2;
      background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
      border-bottom: 1px solid rgba(34,36,38,.15)
  }
`;

interface Props extends MessageSourceProps {
  roles?: Array<RoleDto>,
  userRoles?: Array<UserRoleDto>,
  mandateOptions: Array<DropdownOption>,
  selectedMandate?: number,
  setSelectedUserRoles: (selectedUserRoles: Array<UserRoleDto>) => void
}

interface State {
  roles: Map<string, RoleDto>,
  selectedMandate?: number,
  userAddRoles: Array<UserRoleDto>,
  markedRoles: Array<string>
}

class UserRolesSegment extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {roles, userRoles = [], selectedMandate} = this.props;

    let rolesMap = new Map<string, RoleDto>();
    if (roles) {
      roles.forEach(role => {
        rolesMap.set(role.roleId, role);
      });
    }

    this.state = {
      roles: rolesMap,
      selectedMandate: selectedMandate,
      userAddRoles: userRoles,
      markedRoles: []
    };
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    const {roles, mandateOptions, setSelectedUserRoles, userRoles} = this.props;
    const {userAddRoles, selectedMandate} = this.state;

    if (prevProps.roles !== roles && roles) {
      let rolesMap = new Map<string, RoleDto>();
      roles.forEach(role => {
        rolesMap.set(role.roleId, role);
      });
      this.setState({
        roles: rolesMap
      });
    }

    if (prevProps.mandateOptions !== mandateOptions) {

      if (prevProps.mandateOptions.length > 0) {
        const diff = [...mandateOptions.filter(mandate => !prevProps.mandateOptions.includes(mandate)),
          ...prevProps.mandateOptions.filter(mandate => !mandateOptions.includes(mandate))];

        if (diff.length > 0) {
          const userRoles =
              userAddRoles.filter(userRole => userRole.mandateId !== diff[0].value);

          this.setState({
            userAddRoles: userRoles
          });
          setSelectedUserRoles(userRoles);
        }
      }
      const mandate =
          mandateOptions.find(mandate => mandate.value === selectedMandate);

      if (!mandate && mandateOptions.length > 0) {
        this.setState({
          selectedMandate: mandateOptions[0].value
        });
      }

      if (mandateOptions.length === 0) {

        if (userRoles && userRoles.length > 0) {
          setSelectedUserRoles([]);
        }

        this.setState({
          userAddRoles: [],
          selectedMandate: undefined
        });
      }
    }
  }

  handleMandateChange = (mandateId: number) => {
    this.setState({
      selectedMandate: mandateId,
      markedRoles: []
    });
  };

  markRole = (roleId: string) => {
    const {markedRoles} = this.state;

    if (!markedRoles.includes(roleId)) {
      this.setState({
        markedRoles: [...markedRoles, roleId]
      });
    } else {

      this.setState({
        markedRoles: markedRoles.filter(role => role !== roleId)
      });
    }
  };

  markUnmarkAllRoles = (roleIds: Array<string>) => {
    this.setState({
      markedRoles: roleIds
    });
  };

  addRemoveRole = () => {
    const {setSelectedUserRoles} = this.props;
    const {selectedMandate, markedRoles = [], userAddRoles = []} = this.state;
    const userRoleIds = userAddRoles
        .filter(userRole => userRole.mandateId === selectedMandate)
        .map(userRole => userRole.roleId);

    if (markedRoles.length > 0) {
      if (markedRoles.some(roleId => userRoleIds.includes(roleId))) {
        const filteredUserRoles = userAddRoles
            .filter(userRole => !markedRoles.includes(userRole.roleId)
                || userRole.mandateId !== selectedMandate
            );

        this.setState({
          userAddRoles: filteredUserRoles,
          markedRoles: []
        });
        setSelectedUserRoles(filteredUserRoles);
      } else if (selectedMandate) {
        const userRolesToAdd: Array<UserRoleDto> = markedRoles.map(roleId => {
          return {
            mandateId: selectedMandate,
            roleId: roleId
          }
        });
        const newUserRoles = [...userAddRoles, ...userRolesToAdd];
        this.setState({
          userAddRoles: newUserRoles,
          markedRoles: []
        });
        setSelectedUserRoles(newUserRoles);
      }
    }
  };

  render(): React.ReactNode {
    const {roles, selectedMandate, userAddRoles = [], markedRoles = []} = this.state;
    const {mandateOptions, getMessage} = this.props;

    let userRolesForMandate: Array<string> = [];
    if (selectedMandate && roles.size > 0 && userAddRoles.length > 0) {
      userRolesForMandate = userAddRoles
          .filter(userRole => userRole.mandateId === selectedMandate)
          .map(userRole => userRole.roleId);
    }

    let selectableRolesForMandate: Array<string> = [];
    if (selectedMandate && roles.size > 0) {
      roles.forEach((role, roleId) => {
        if (!userRolesForMandate.includes(roleId)) {
          selectableRolesForMandate.push(roleId);
        }
      });
    }

    const disabledLeft = markedRoles.length === 0
        || !selectedMandate
        || selectableRolesForMandate.some(role => markedRoles.includes(role));

    const disabledRight = markedRoles.length === 0
        || !selectedMandate
        || userRolesForMandate.some(role => markedRoles.includes(role));

    return (
        <React.Fragment>
          <Grid.Row style={{marginTop: "1rem"}}>
            <Grid.Column>
              <Header>{getMessage("header.navigation.roles")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("user.rolesForMandate")}:
            </Grid.Column>
            <Grid.Column width={6}>
              <PxDropdownSc
                  value={selectedMandate}
                  onChange={this.onMandateDropdownChange}
                  options={mandateOptions}
                  selection
                  search
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <StyledTableLeft>
              {this.renderTableSegment(getMessage("user.availableRoles"), selectableRolesForMandate)}
            </StyledTableLeft>
            <StyledButtons textAlign='center' verticalAlign='middle'>
              <StyledBtnContainer>
                <StyledIcon bordered
                            onClick={() => this.markUnmarkAllRoles(selectableRolesForMandate)}
                            name='clipboard list'
                />
                <StyledIcon bordered
                            onClick={() => this.markUnmarkAllRoles(userRolesForMandate)}
                            name='clipboard list'
                />
              </StyledBtnContainer>
              <StyledBtnContainer>
                <StyledIcon bordered
                            onClick={() => this.markUnmarkAllRoles([])}
                            name='clipboard outline'
                />
                <StyledIcon bordered
                            onClick={() => this.markUnmarkAllRoles([])}
                            name='clipboard outline'
                />
              </StyledBtnContainer>
              <StyledBtnContainer>
                <StyledIcon bordered
                            disabled={disabledRight}
                            onClick={this.addRemoveRole}
                            name='angle double right'
                />
                <StyledIcon bordered
                            disabled={disabledLeft}
                            onClick={this.addRemoveRole}
                            name='angle double left'
                />
              </StyledBtnContainer>
            </StyledButtons>
            <StyledTableRight>
              {this.renderTableSegment(getMessage("user.selectedRoles"), userRolesForMandate)}
            </StyledTableRight>
          </Grid.Row>
        </React.Fragment>
    );
  }

  onMandateDropdownChange = (e: any, {value}: DropdownProps) => {
    if (value && typeof value === 'number') {
      this.handleMandateChange(value)
    }
  };

  renderTableSegment = (headerText: string, roleIds: Array<string>): React.ReactNode => {
    const {roles, markedRoles} = this.state;

    return (
        <React.Fragment>
          <Header as='h5'>
            {headerText}:
          </Header>
          <ScrollableTableWrapper>
            <Table celled selectable size='small'>
              <Table.Body>
                {roles.size > 0 && roleIds.map((roleId: string) => (
                        <StyledTableRow key={roleId}
                                        onClick={() => this.markRole(roleId)}
                                        active={markedRoles.includes(roleId)}
                        >
                          <Table.Cell>
                            {
                              // @ts-ignore
                              roles.get(roleId).name
                            }
                          </Table.Cell>
                        </StyledTableRow>
                    )
                )}
              </Table.Body>
            </Table>
          </ScrollableTableWrapper>
        </React.Fragment>
    )
  };
}

export default withMessages(UserRolesSegment);
